import { z } from "zod";
import { prescriptionOrderSchema } from "./prescription-cycle";

// TES-1241 (TES-1240, A-02 "KANONISKT BESLUT 2026-10-11", §4) — the Recept
// section of "Granska receptövertag":
//   "Vid receptövertag förifylls den från F-07 med patientens uppgifter om pågående läkemedel, styrka, dos, intervall, behandlingsstart, receptets giltighet och uppladdat recept- eller ordinationsunderlag."
//   "Uppgifterna märks som patientrapporterade eller underlag att verifiera tills läkaren har granskat dom. Läkaren ska kunna Bekräfta som planerad behandling, Ändra planerad behandling, markera Befintligt recept gäller fortfarande eller ange Nytt e-recept behöver utfärdas."
//   "Verifierade receptuppgifter återanvänds som Planerad läkemedelsbehandling under Åtgärd och behandlingsplan."
//
// The four choices are two questions: what the planned treatment is (her
// reported one, or a change), and whether her existing prescription carries
// on or a new e-prescription is issued in Webdoc. "E-recept benämns aldrig
// remiss."

export const TRANSFER_RECEPT_PLAN_CHOICES = ["confirmed", "changed"] as const;
export const transferReceptPlanChoiceSchema = z.enum(
  TRANSFER_RECEPT_PLAN_CHOICES,
);
export type TransferReceptPlanChoice = z.infer<
  typeof transferReceptPlanChoiceSchema
>;

export const TRANSFER_RECEPT_PLAN_CHOICE_LABEL: Record<
  TransferReceptPlanChoice,
  string
> = {
  confirmed: "Bekräfta som planerad behandling",
  changed: "Ändra planerad behandling",
};

// TES-1242 (TES-1240, A-02 2026-10-11 §6) — the same two choices when the
// case comes back after kompletteringar with a plan already saved:
//   "…den tidigare planerade behandlingen förifylld samt valen Behandlingen gäller fortfarande och Ändra behandlingen."
export const TRANSFER_RECEPT_RETURN_CHOICE_LABEL: Record<
  TransferReceptPlanChoice,
  string
> = {
  confirmed: "Behandlingen gäller fortfarande",
  changed: "Ändra behandlingen",
};

// TES-1242 — A-02 2026-10-11 §6, verbatim: on a komplettering the plan is
// saved, never prescribed.
export const TRANSFER_RECEPT_SAVED_WITH_COMPLETION =
  "Receptuppgifterna och den planerade behandlingen sparas, men ett nytt e-recept utfärdas inte förrän det nödvändiga underlaget har bedömts.";

// TES-1242 — the Recept section's planned medication sent with "Begär
// komplettering" or "Nytt blodprov krävs": the order, strength and plan, and
// whether the doctor kept the saved plan or changed it (with his reason).
// Saved as the planned medication; no prescription is registered.
export const transferPlannedMedicationSchema = prescriptionOrderSchema.extend({
  strength: z.string().trim().max(64).optional(),
  plan: z.string().trim().max(2000).optional(),
  plannedTreatment: transferReceptPlanChoiceSchema,
  planChangeReason: z.string().trim().max(2000).optional(),
});
export type TransferPlannedMedication = z.infer<
  typeof transferPlannedMedicationSchema
>;

export const TRANSFER_RECEPT_PRESCRIPTION_NEEDS = [
  "existing_valid",
  "new_e_prescription",
] as const;
export type TransferReceptPrescriptionNeed =
  (typeof TRANSFER_RECEPT_PRESCRIPTION_NEEDS)[number];

export const TRANSFER_RECEPT_PRESCRIPTION_NEED_LABEL: Record<
  TransferReceptPrescriptionNeed,
  string
> = {
  existing_valid: "Befintligt recept gäller fortfarande",
  new_e_prescription: "Nytt e-recept behöver utfärdas",
};

// The prescription status each need is confirmed with in the approval's
// Webdoc step (TES-1187): an existing prescription as it stands, a new one
// once "Receptet är utfärdat i Webdoc".
export const TRANSFER_RECEPT_NEED_PRESCRIPTION_CHOICE = {
  existing_valid: "existing_valid",
  new_e_prescription: "new_issued",
} as const satisfies Record<TransferReceptPrescriptionNeed, string>;

// Her values carry this tag until the doctor has chosen, from Emelie's
// "patientrapporterade eller underlag att verifiera".
export const TRANSFER_RECEPT_REPORTED_TAG =
  "Patientrapporterat – att verifiera";

// Her current treatment as F-07 states it (transferReportedTreatment): the
// answers' text, null where she said nothing.
export const transferReportedTreatmentSchema = z.object({
  medication: z.string().nullable(),
  strength: z.string().nullable(),
  dose: z.string().nullable(),
  frequency: z.string().nullable(),
  treatmentStart: z.string().nullable(),
  prescriptionValidUntil: z.string().nullable(),
});
export type TransferReportedTreatment = z.infer<
  typeof transferReportedTreatmentSchema
>;
